import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, Download, FileText, FileType, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Help / User Guide page — fetches the canonical markdown guide from /docs/USER_GUIDE.md
 * (served by CRA from public/) and renders it with screenshots inline. Provides a
 * download link so users can save the guide for offline reference.
 */
export default function Help() {
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [docxBusy, setDocxBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadAs = async (kind) => {
    const conf = {
      docx: {
        url: "/docs/user-guide.docx",
        filename: "User_Guide.docx",
        mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        setBusy: setDocxBusy,
      },
      pdf: {
        url: "/docs/user-guide.pdf",
        filename: "User_Guide.pdf",
        mime: "application/pdf",
        setBusy: setPdfBusy,
      },
    }[kind];
    if (!conf) return;
    conf.setBusy(true);
    try {
      const res = await api.get(conf.url, { responseType: "blob" });
      const blob = res.data instanceof Blob && res.data.type
        ? res.data
        : new Blob([res.data], { type: conf.mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = conf.filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setError(`Could not generate the ${kind.toUpperCase()} document. Try the other format.`);
    } finally {
      conf.setBusy(false);
    }
  };

  const downloadDocx = () => downloadAs("docx");
  const downloadPdf = () => downloadAs("pdf");

  useEffect(() => {
    fetch("/docs/USER_GUIDE.md")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => setMd(t))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Documentation
          </div>
          <h1 className="font-display text-3xl uppercase text-slate-900 tracking-tight">User Guide</h1>
          <p className="text-sm text-slate-600 mt-1">
            Everything you need to receive, prepare, and deliver shipments — with screenshots.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={pdfBusy}
            data-testid="download-guide-pdf-btn"
            className="rg-tap px-4 border-2 border-rose-700 hover:border-rose-900 rounded-sm bg-rose-700 hover:bg-rose-800 text-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm disabled:opacity-60"
            title="Download as PDF — opens everywhere, no Word required"
          >
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileType className="h-4 w-4" />}
            {pdfBusy ? "Building…" : "Download (.pdf)"}
          </button>
          <button
            type="button"
            onClick={downloadDocx}
            disabled={docxBusy}
            data-testid="download-guide-docx-btn"
            className="rg-tap px-4 border-2 border-blue-700 hover:border-blue-900 rounded-sm bg-blue-700 hover:bg-blue-800 text-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm disabled:opacity-60"
            title="Download as Microsoft Word (.docx) with all screenshots embedded"
          >
            {docxBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {docxBusy ? "Building…" : "Download (.docx)"}
          </button>
          <a
            href="/docs/USER_GUIDE.md"
            download="User_Guide.md"
            data-testid="download-guide-btn"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-slate-700"
            title="Download the raw markdown source"
          >
            <Download className="h-4 w-4" />
            .md
          </a>
        </div>
      </div>

      <div className="rg-card p-5 sm:p-8" data-testid="user-guide-content">
        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading guide…
          </div>
        )}
        {error && (
          <div className="text-rose-700 font-bold text-sm">
            Failed to load the guide: {error}
          </div>
        )}
        {!loading && !error && (
          <article className="rg-guide-prose">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: ({ node, ...props }) => {
                  // Markdown uses relative paths like "screenshots/x.png" which
                  // would resolve against /help. Rewrite to the canonical /docs/ root.
                  let src = props.src || "";
                  if (src && !/^(https?:|\/)/i.test(src)) {
                    src = `/docs/${src.replace(/^\.\//, "")}`;
                  }
                  return (
                    <img
                      {...props}
                      src={src}
                      loading="lazy"
                      className="w-full max-w-3xl border-2 border-slate-200 rounded-sm my-4"
                    />
                  );
                },
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    target={props.href?.startsWith("http") ? "_blank" : undefined}
                    rel={props.href?.startsWith("http") ? "noreferrer noopener" : undefined}
                    className="text-amber-700 hover:text-amber-900 underline font-bold"
                  />
                ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto my-3">
                    <table {...props} className="w-full text-sm border-2 border-slate-200" />
                  </div>
                ),
                th: ({ node, ...props }) => (
                  <th
                    {...props}
                    className="text-left text-xs uppercase tracking-wider text-slate-600 bg-slate-50 border-b-2 border-slate-200 px-3 py-2"
                  />
                ),
                td: ({ node, ...props }) => (
                  <td {...props} className="border-b border-slate-100 px-3 py-2 align-top text-slate-700" />
                ),
                code: ({ inline, ...props }) =>
                  inline ? (
                    <code
                      {...props}
                      className="bg-slate-100 px-1.5 py-0.5 rounded-sm text-[13px] font-mono text-slate-800"
                    />
                  ) : (
                    <code {...props} className="block bg-slate-900 text-amber-200 p-3 rounded-sm text-xs font-mono overflow-x-auto" />
                  ),
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    {...props}
                    className="border-l-4 border-amber-500 bg-amber-50 px-4 py-2 my-3 text-slate-800"
                  />
                ),
                h1: ({ node, ...props }) => (
                  <h1 {...props} className="font-display text-3xl uppercase text-slate-900 tracking-tight mt-6 mb-3" />
                ),
                h2: ({ node, ...props }) => (
                  <h2 {...props} className="font-display text-2xl uppercase text-slate-900 tracking-tight mt-8 mb-3 pt-4 border-t-2 border-slate-200" />
                ),
                h3: ({ node, ...props }) => (
                  <h3 {...props} className="font-display text-lg uppercase text-slate-800 mt-5 mb-2" />
                ),
                p: ({ node, ...props }) => <p {...props} className="text-slate-700 leading-relaxed my-2" />,
                ul: ({ node, ...props }) => <ul {...props} className="list-disc list-inside space-y-1 my-2 text-slate-700" />,
                ol: ({ node, ...props }) => <ol {...props} className="list-decimal list-inside space-y-1 my-2 text-slate-700" />,
                hr: ({ node, ...props }) => <hr {...props} className="my-6 border-slate-200" />,
              }}
            >
              {md}
            </ReactMarkdown>
          </article>
        )}
      </div>
    </div>
  );
}
